// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type Facility,
  type FacilityId,
  type ShopConfiguration,
  useEvolveDelete,
  useEvolveGet,
  useEvolvePatch,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { LoadingOverlay } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Navigate, useParams } from 'react-router';

import { useAppShell } from 'app/Layout/NavBar/useAppShell';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useTaskTypes } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/TaskTypes/useTaskTypes';

import { useWorkCellsGrid } from './useWorkCellsGrid';
import { WorkCellSetupContext, type WorkCellSetupContextType } from './WorkCellSetupContext';

const ShopSetupWrapperWithShopConfig = ({
  children,
  facility,
  activeShopConfiguration,
  refreshShopConfigurations,
}: {
  children: ReactNode;
  facility: Facility;
  activeShopConfiguration: ShopConfiguration;
  refreshShopConfigurations: () => Promise<ShopConfiguration[]>;
}) => {
  const { facilityId } = facility;
  const { navbarWidth } = useAppShell();
  const {
    groupsPaginatedGet: { reset: resetTaskTypeGroups },
  } = useTaskTypes();
  const [draftShopConfiguration, setDraftShopConfiguration] = useState<ShopConfiguration | undefined>();

  const draftConfigUrl = useMemo(() => `shop/configuration/${facilityId}/draft` as const, [facilityId]);
  const { data: draftShopConfigurationFromNetwork, loading: loadingDraftShopConfiguration } = useEvolveGet(
    draftConfigUrl,
    { lazy: false },
  );
  useEffect(() => {
    if (isNotNil(draftShopConfigurationFromNetwork)) {
      setDraftShopConfiguration(draftShopConfigurationFromNetwork);
    }
  }, [draftShopConfigurationFromNetwork]);

  /** `serverSideGridProps` specifically for the "Draft" table */
  const serverSideGridProps = useWorkCellsGrid(
    facilityId,
    'draft',
    draftShopConfiguration,
    isNotNil(draftShopConfiguration),
  );

  const { apiCall: createWorkCellApiCall, loading: creating } = useEvolvePost('shop/workCell');
  const { apiCall: updateWorkCellApiCall, loading: updating } = useEvolvePatch('shop/workCell/:workCellId');
  const { apiCall: deleteWorkCellApiCall, loading: deleting } = useEvolveDelete('shop/workCell/:workCellId');

  const { apiCall: createWorkCellTaskTypesApiCall, loading: creatingTaskType } = useEvolvePost('shop/workCellTaskType');

  const { apiCall: deleteWorkCellTaskTypeApiCall, loading: deletingTaskType } = useEvolveDelete(
    'shop/workCellTaskType/:workCellTaskTypeId',
  );

  const createWorkCell = useCallback<WorkCellSetupContextType['createWorkCell']>(
    async (newWorkCell, shopConfigurationId) =>
      createWorkCellApiCall({ ...newWorkCell, shopConfigurationId, facilityId }),
    [createWorkCellApiCall, facilityId],
  );
  const updateWorkCell = useCallback<WorkCellSetupContextType['updateWorkCell']>(
    async ({ workCellId, ...workCell }) => updateWorkCellApiCall(workCell, { url: `shop/workCell/${workCellId}` }),
    [updateWorkCellApiCall],
  );
  const deleteWorkCell = useCallback<WorkCellSetupContextType['deleteWorkCell']>(
    async (workCellId) => {
      await deleteWorkCellApiCall({ url: `shop/workCell/${workCellId}` }).then(() => resetTaskTypeGroups());
    },
    [deleteWorkCellApiCall, resetTaskTypeGroups],
  );
  const mapWorkCellTaskTypesAndGroups = useCallback<WorkCellSetupContextType['mapWorkCellTaskTypesAndGroups']>(
    async (workCellId, taskTypeIds, taskTypeGroupIds) =>
      createWorkCellTaskTypesApiCall({ workCellId, taskTypeIds, taskTypeGroupIds }),
    [createWorkCellTaskTypesApiCall],
  );
  const unmapWorkCellTaskType = useCallback<WorkCellSetupContextType['unmapWorkCellTaskType']>(
    async (workCellTaskTypeId) => {
      await deleteWorkCellTaskTypeApiCall({ url: `shop/workCellTaskType/${workCellTaskTypeId}` });
    },
    [deleteWorkCellTaskTypeApiCall],
  );

  const { apiCall: createDraftShopConfigApiCall, loading: creatingDraft } = useEvolvePost(draftConfigUrl);
  const { apiCall: deleteDraftShopConfigApiCall, loading: deletingDraft } = useEvolveDelete(draftConfigUrl);
  const { apiCall: applyDraftShopConfigApiCall, loading: applyingDraft } = useEvolvePost(
    `${draftConfigUrl}/set-current` as const,
  );

  const createDraftShopConfiguration = useCallback(async () => {
    await createDraftShopConfigApiCall({}).then(setDraftShopConfiguration);
  }, [createDraftShopConfigApiCall]);
  const deleteDraftShopConfiguration = useCallback(async () => {
    await deleteDraftShopConfigApiCall().then(() => {
      setDraftShopConfiguration(undefined);
      notifications.show({
        title: 'Discarded changes',
        message: 'Work Cell Setup changes discarded.',
        color: 'green',
      });
    });
  }, [deleteDraftShopConfigApiCall]);
  const applyDraftShopConfiguration = useCallback(async () => {
    await applyDraftShopConfigApiCall({}).then(() => {
      setDraftShopConfiguration(undefined);
      void refreshShopConfigurations();
    });
  }, [applyDraftShopConfigApiCall, refreshShopConfigurations]);
  const bigMutationRunning = creatingDraft || deletingDraft || applyingDraft;
  const mutationRunning =
    creating || updating || deleting || creatingTaskType || deletingTaskType || bigMutationRunning;

  return (
    <WorkCellSetupContext
      value={useMemo(
        () => ({
          activeShopConfiguration,
          draftShopConfiguration,
          loadingDraftShopConfiguration,
          createDraftShopConfiguration,
          deleteDraftShopConfiguration,
          applyDraftShopConfiguration,
          facility,
          serverSideGridProps,
          createWorkCell,
          updateWorkCell,
          deleteWorkCell,
          mapWorkCellTaskTypesAndGroups,
          unmapWorkCellTaskType,
          mutationRunning,
        }),
        [
          activeShopConfiguration,
          draftShopConfiguration,
          loadingDraftShopConfiguration,
          createDraftShopConfiguration,
          deleteDraftShopConfiguration,
          applyDraftShopConfiguration,
          facility,
          serverSideGridProps,
          createWorkCell,
          updateWorkCell,
          deleteWorkCell,
          mapWorkCellTaskTypesAndGroups,
          unmapWorkCellTaskType,
          mutationRunning,
        ],
      )}
    >
      {children}
      <LoadingOverlay zIndex={1} visible={bigMutationRunning || loadingDraftShopConfiguration} ml={navbarWidth} />
    </WorkCellSetupContext>
  );
};

export const ShopSetupWrapper = ({ children }: { children: ReactNode }) => {
  const { facilityId: facilityIdRaw } = useParams();
  const facilityId = facilityIdRaw as FacilityId;
  const { data: facility, loading: loadingFacility } = useEvolveGet(`shop/facility/${facilityId}`, { lazy: false });
  const {
    data: shopConfigurations,
    loading: loadingShopConfigurations,
    apiCall: refreshShopConfigurations,
  } = useEvolveGet(`shop/configuration/${facilityId}`, { lazy: false });
  if (loadingFacility || loadingShopConfigurations) {
    return <LoadingOverlay visible />;
  }
  const activeShopConfiguration = shopConfigurations?.find((c) => c.isActive);
  if (isNil(activeShopConfiguration) || isNil(facility)) {
    // Should never happen in production,
    // but can happen in lower lifecycles due to bad seed data
    return <Navigate to="/not-found" />;
  }

  return (
    <ShopSetupWrapperWithShopConfig
      facility={facility}
      activeShopConfiguration={activeShopConfiguration}
      refreshShopConfigurations={refreshShopConfigurations}
    >
      {children}
    </ShopSetupWrapperWithShopConfig>
  );
};
